import { createContext, useContext, useMemo, useRef } from 'react';

type PopoverGroupId = symbol;

type PopoverGroupElement = {
    element: Element;
    parentId: PopoverGroupId | null;
};

type PopoverGroup = {
    registerPopoverElement: (id: PopoverGroupId, parentId: PopoverGroupId | null, element: Element) => () => void;
    containsDescendantElement: (id: PopoverGroupId, element: Node) => boolean;
};

type PopoverGroupContextValue = {
    group: PopoverGroup;
    currentPopoverId: PopoverGroupId | null;
};

export const RomaPopoverGroupContext = createContext<PopoverGroupContextValue | null>(null);

/**
 * Tracks Roma popovers as a hierarchy so outside dismissal can distinguish ancestors and descendants.
 */
export const usePopoverGroup = () => {
    const popoverIdRef = useRef<PopoverGroupId>(Symbol('RomaPopover'));
    const parentPopoverGroup = useContext(RomaPopoverGroupContext);
    const popoverGroupElementsRef = useRef(new Map<PopoverGroupId, PopoverGroupElement>());
    const localPopoverGroup = useMemo<PopoverGroup>(
        () => ({
            registerPopoverElement: (id, parentId, element) => {
                popoverGroupElementsRef.current.set(id, { element, parentId });

                return () => popoverGroupElementsRef.current.delete(id);
            },
            containsDescendantElement: (id, element) =>
                Array.from(popoverGroupElementsRef.current.entries()).some(([popoverId, popover]) => {
                    let currentParentId = popoverGroupElementsRef.current.get(popoverId)?.parentId;

                    // Walk the React popover tree to identify DOM inside nested descendant popovers.
                    while (currentParentId) {
                        if (currentParentId === id) return popover.element.contains(element);

                        currentParentId = popoverGroupElementsRef.current.get(currentParentId)?.parentId;
                    }

                    return false;
                }),
        }),
        [],
    );

    return {
        popoverId: popoverIdRef.current,
        popoverGroup: parentPopoverGroup?.group ?? localPopoverGroup,
        parentPopoverId: parentPopoverGroup?.currentPopoverId ?? null,
    };
};
